/* eslint-disable max-len */
/* eslint-disable no-console */
import { API } from 'aws-amplify';

/**
 * TypeScript interfaces for article management
 */
export interface UserInfo {
  userId: string;
  name: string;
}

export interface AccessControl {
  internalEmployees: {
    allSelected: boolean;
    departments?: { name: string; count: number }[];
    specificUsers?: {
      userId: string;
      email: string;
      name: string;
      role: string;
    }[];
  };
  userGroups?: { type: string; count: number }[];
  participantCount?: number;
  participantLimit?: number;
}

export interface EngagementMetrics {
  views: number;
  downloads: number;
  helpful: number;
  notHelpful: number;
  averageTimeSpent: number;
  readCount: number;
  shareCount: number;
}

export interface VersionHistory {
  version: number;
  content: string;
  updatedAt: string;
  updatedBy: UserInfo;
}

export interface Article {
  _id: string;
  companyId: string;
  companyName?: string;
  title: string;
  summary?: string;
  rich_content?: string;
  estimatedReadTime?: number;
  category?: string;
  subCategory?: string;
  tags?: string[];
  author_id: string;
  status: 'draft' | 'scheduled' | 'published' | 'unpublished';
  visibility: 'public' | 'private';
  allowDownload: boolean;
  allowShare: boolean;
  publishedAt?: string;
  scheduledPublishDate?: string;
  version: number;
  accessControl?: AccessControl;
  engagement?: EngagementMetrics;
  versions?: VersionHistory[];
  createdBy?: UserInfo;
  updatedBy?: UserInfo;
  publishedBy?: UserInfo;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateArticlePayload {
  companyId: string;
  companyName?: string;
  title: string;
  summary?: string;
  rich_content?: string;
  category?: string;
  subCategory?: string;
  tags?: string[];
  visibility?: 'public' | 'private';
  allowDownload?: boolean;
  allowShare?: boolean;
  accessControl?: AccessControl;
}

export interface UpdateArticlePayload {
  title?: string;
  summary?: string;
  rich_content?: string;
  category?: string;
  subCategory?: string;
  tags?: string[];
  visibility?: 'public' | 'private';
  allowDownload?: boolean;
  allowShare?: boolean;
  accessControl?: AccessControl;
}

export interface ArticleListParams {
  status?: 'draft' | 'scheduled' | 'published' | 'unpublished';
  category?: string;
  subCategory?: string;
  tag?: string;
  search?: string;
  author_id?: string;
  page?: number;
  limit?: number;
  sort?: string;
}

export interface ArticleListResponse {
  data: Article[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface PublishArticleParams {
  scheduledPublishDate?: string; // ISO date string
  status?: 'scheduled' | 'published' | 'unpublished';
}

export interface EngageArticleParams {
  metric: 'helpful' | 'notHelpful' | 'readCount' | 'shareCount' | 'downloads';
  value: number;
}

// API endpoint name - this should match the API name configured in Amplify
const API_NAME = 'resourceManagementADI';

/**
 * Create a new article
 * @param payload Article creation parameters
 * @returns Promise with created article
 */
export const createArticle = async (
	payload: CreateArticlePayload
): Promise<Article> =>
{
	try
	{
		const response = await API.post(API_NAME, '/articles', {
			body : payload
		});

		return response.data;
	}
	catch (error)
	{
		console.error('Error creating article:', error);
		throw error;
	}
};

/**
 * Get a single article by ID
 * @param id Article identifier
 * @returns Promise with article data
 */
export const getArticleById = async (id: string, queryStringParameters): Promise<Article> =>
{
	try
	{
		const response = await API.get(API_NAME, `/articles/${id}`, { queryStringParameters });

		return response.data;
	}
	catch (error)
	{
		console.error('Error fetching article:', error);
		throw error;
	}
};

/**
 * Update an existing article
 * @param id Article identifier
 * @param payload Update parameters
 * @returns Promise with updated article
 */
export const updateArticle = async (
	id: string,
	payload: UpdateArticlePayload
): Promise<Article> =>
{
	try
	{
		const response = await API.put(API_NAME, `/articles/${id}`, {
			body : payload
		});

		return response.data;
	}
	catch (error)
	{
		console.error('Error updating article:', error);
		throw error;
	}
};

/**
 * Delete an article
 * @param id Article identifier
 * @returns Promise with success message
 */
export const deleteArticle = async (id: string): Promise<{ success: boolean; message: string }> =>
{
	try
	{
		const response = await API.del(API_NAME, `/articles/${id}`, {});

		return response;
	}
	catch (error)
	{
		console.error('Error deleting article:', error);
		throw error;
	}
};

/**
 * Publish an article or schedule it for publication
 * @param id Article identifier
 * @param params Optional scheduled publish date
 * @returns Promise with published article data
 */
export const publishArticle = async (
	id: string,
	params?: PublishArticleParams
): Promise<Article> =>
{
	try
	{
		const response = await API.post(API_NAME, `/articles/${id}/publish`, {
			body : params || {}
		});

		return response.data;
	}
	catch (error)
	{
		console.error('Error publishing article:', error);
		throw error;
	}
};

/**
 * Track a view for an article
 * @param id Article identifier
 * @returns Promise with success message
 */
export const trackArticleView = async (
	id: string
): Promise<{ success: boolean; message: string }> =>
{
	try
	{
		const response = await API.post(API_NAME, `/articles/${id}/view`, {});

		return response;
	}
	catch (error)
	{
		console.error('Error tracking article view:', error);
		throw error;
	}
};

/**
 * Track time spent reading an article
 * @param articleId - Article ID
 * @param body - Time spent reading the article
 * @returns Promise with API response
 */
export const trackArticleTimeSpent = async (
	articleId: string,
	body: {
		timeSpent: number;
	}
): Promise<{ success: boolean; data?: any }> =>
{
	try
	{
		const response = await API.post(
			API_NAME,
			`/articles/${articleId}/time-spent`,
			{
				body
			}
		);

		return {
			success : true,
			data    : response
		};
	}
	catch (error)
	{
		console.error('Error tracking article time spent:', error);

		return {
			success : false
		};
	}
};

/**
 * Update engagement metrics for an article
 * @param id Article identifier
 * @param params Engagement metric and value
 * @returns Promise with updated engagement data
 */
export const updateArticleEngagement = async (
	id: string,
	metric: 'helpful' | 'notHelpful' | 'readCount' | 'shareCount' | 'downloads' | 'averageTimeSpent',
	value: number
): Promise<{ success: boolean; data: { engagement: EngagementMetrics }; message: string }> =>
{
	try
	{
		const response = await API.post(API_NAME, `/articles/${id}/engage`, {
			body : { metric, value }
		});

		return response;
	}
	catch (error)
	{
		console.error('Error updating article engagement:', error);
		throw error;
	}
};

/**
 * Toggle favorite status for an article
 * @param companyId - Company ID
 * @param articleId - Article ID
 * @returns Promise with favorite status and updated article
 */
export const toggleArticleFavorite = async (
	companyId: string,
	articleId: string
): Promise<{ success: boolean; data: { article: any; isFavorited: boolean; favoriteCount: number }; message: string }> =>
{
	try
	{
		const response = await API.post(API_NAME, `/articles/company/${companyId}/${articleId}/favorite`, {});

		return response;
	}
	catch (error)
	{
		console.error('Error toggling article favorite:', error);
		throw error;
	}
};